// src/pages/vendor/Customers.jsx - MOBILE RESPONSIVE FIXED
import React, { useEffect, useState } from 'react';
import { useVendor } from '../../hooks/useVendor';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Search,
  People,
  ShoppingBag,
  AttachMoney,
  Email,
  Phone,
  Refresh,
  Download,
  Star,
  ExpandMore,
  ExpandLess,
  Person,
} from '@mui/icons-material';

const Customers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { customers, loading, error, fetchCustomers } = useVendor();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const toggleCustomerExpand = (customerId) => {
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  const filteredCustomers = customers?.filter((customer) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const user = customer?.user || {};
    return (
      (user?.name || '').toLowerCase().includes(searchLower) ||
      (user?.email || '').toLowerCase().includes(searchLower) ||
      (user?.phone || '').includes(searchTerm)
    );
  });

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const totalCustomers = customers?.length || 0;
  const totalOrders = customers?.reduce((sum, c) => sum + (c?.totalOrders || 0), 0) || 0;
  const totalRevenue = customers?.reduce((sum, c) => sum + (c?.totalSpent || 0), 0) || 0;
  const loyalCustomers = customers?.filter(c => (c?.totalOrders || 0) > 5).length || 0;

  // Mobile Customer Card
  const renderMobileCustomerCard = (customer, index) => {
    const user = customer?.user || {};
    const key = customer?.id || user?.id || `customer-${index}`;
    const isExpanded = expandedCustomer === key;

    return (
      <Card key={key} sx={{ mb: 1.5, borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Stack spacing={1.5}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  {user?.name ? getInitials(user.name) : '?'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {user?.name || 'Unknown Customer'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {user?.id || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label="Active"
                size="small"
                color="success"
                variant="filled"
                sx={{ height: 20, fontSize: '0.6rem' }}
              />
            </Stack>

            {/* Stats */}
            <Stack direction="row" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <ShoppingBag fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="600">
                  {customer?.totalOrders || 0} orders
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AttachMoney fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="600" color="primary">
                  ${(customer?.totalSpent || 0).toFixed(2)}
                </Typography>
              </Stack>
            </Stack>

            {/* Expand Button */}
            <Button
              size="small"
              variant="outlined"
              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => toggleCustomerExpand(key)}
              sx={{ textTransform: 'none', fontSize: '0.7rem' }}
            >
              {isExpanded ? 'Hide Details' : 'Show Details'}
            </Button>

            {/* Expanded Details */}
            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{user?.email || 'N/A'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">{user?.phone || 'N/A'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2">
                    Joined: {customer?.lastOrderDate
                      ? new Date(customer.lastOrderDate).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Stack>
                {customer?.totalOrders > 5 && (
                  <Chip
                    icon={<Star />}
                    label="Loyal Customer"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Customers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your customer relationships
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchCustomers}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards - Responsive */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <People color="primary" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {totalCustomers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Customers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <ShoppingBag color="success" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {totalOrders}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <AttachMoney color="warning" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                ${totalRevenue.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Star sx={{ fontSize: { xs: 28, sm: 40 }, color: 'secondary.main' }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {loyalCustomers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Loyal Customers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Search customers by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isMobile ? 'small' : 'medium'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Customers List - Mobile Cards or Desktop Table */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          {!filteredCustomers || filteredCustomers.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm ? 'No customers match your search' : 'No customers found'}
              </Typography>
              {!searchTerm && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Customers will appear here once they place orders from your store
                </Typography>
              )}
            </Paper>
          ) : (
            filteredCustomers.map((customer, index) => renderMobileCustomerCard(customer, index))
          )}
        </Box>
      ) : (
        // Desktop Table View
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Customer</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Contact</Typography></TableCell>
                  <TableCell align="center"><Typography variant="subtitle2" fontWeight="bold">Orders</Typography></TableCell>
                  <TableCell align="center"><Typography variant="subtitle2" fontWeight="bold">Total Spent</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Joined</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!filteredCustomers || filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'No customers match your search' : 'No customers found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer, index) => {
                    const user = customer?.user || {};
                    const key = customer?.id || user?.id || `customer-${index}`;

                    return (
                      <TableRow key={key} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {user?.name ? getInitials(user.name) : '?'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {user?.name || 'Unknown Customer'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {user?.id || 'N/A'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Email fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {user?.email || 'N/A'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {user?.phone || 'N/A'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="600">
                            {customer?.totalOrders || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="600" color="primary">
                            ${(customer?.totalSpent || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {customer?.lastOrderDate
                              ? new Date(customer.lastOrderDate).toLocaleDateString()
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default Customers;